import { Button, Checkbox, FormControlLabel, Grid, OutlinedInput, Stack, Typography, useTheme } from "@mui/material";
import FormSection from "../../Forms/FormSection/form-section";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { useEffect, useMemo, useState } from "react";
import { orderService } from "../../../Core/Api/services";
import { AddCircleOutlineRounded, RemoveCircleOutlineRounded } from "@mui/icons-material";
import { DbDeliveryBroadcasterInfos } from "../../../Core/Api/Types/db-delivery-broadcaster-infos";
import { produce } from "immer";
import { ConfigCard } from "./rating-desc-values-form";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { DbMetadataFieldValueInfos } from "../../../Core/Api/Types/db-metadata-field-value-infos";
import { useTranslation } from "react-i18next";

export type GenreValuesFormProps = {
  metadata: DbMetadataFieldInfos;
  setMetadata: React.Dispatch<React.SetStateAction<DbMetadataFieldInfos>>;
};

const GenreValuesForm: React.FC<GenreValuesFormProps> = (props: GenreValuesFormProps) => {
  const { metadata, setMetadata } = props;

  const theme = useTheme();
  const { t } = useTranslation();

  const [broadcasters, setBroadcasters] = useState<DbDeliveryBroadcasterInfos[]>([]);
  const [selectedvalueIndex, setSelectedValueIndex] = useState<number>(-1);
  const [filterEnabledBroadcasters, setFilterBroadcastersWithData] = useState<boolean>(false);

  const handleFilterEnabledBroadcastersChange = (e: any) => {
    setFilterBroadcastersWithData(e.target.checked);
  };

  const currentValue = useMemo(
    () =>
      metadata.values && metadata.values[selectedvalueIndex]
        ? metadata.values[selectedvalueIndex].valueObject?.value ?? null
        : null,
    [selectedvalueIndex, metadata.values?.length, metadata]
  );

  useEffect(() => {
    orderService.GetBroadcastersBundlemakerList().then((response) => {
      if (response && response.broadcasters) {
        setBroadcasters(response.broadcasters.sort((a, b) => a.name!.localeCompare(b.name!)));
      }
    });
  }, []);

  return (
    <FormSection title={t("form:section.title.genreValues")} status="todo">
      <Grid
        sx={{
          p: 1,
          border: `1px solid ${theme.palette.background.alphaHover}`,
          background: theme.palette.background.default,
          borderRadius: 1,
        }}
        display={"flex"}
        direction={"row"}
        container
      >
        <Grid item md={5}>
          <Stack spacing={1} direction={"column"}>
            {currentValue && (
              <OutlinedInput
                value={currentValue.name ?? ""}
                onChange={(e) => {
                  setMetadata(
                    produce((draft) => {
                      if (!draft?.values) return;
                      if (!draft.values[selectedvalueIndex]) return;
                      const currMeta = draft.values[selectedvalueIndex];
                      const currValue = currMeta?.valueObject?.value;
                      if (!currValue) return;
                      const val = e.target.value;
                      currValue.name = val;
                      currMeta.label = val;
                      currValue.label = val;
                    })
                  );
                }}
                sx={{ mr: "12px !important" }}
              />
            )}
            <Button
              onClick={() => {
                setMetadata(
                  produce((draft) => {
                    if (!draft.values) return;
                    draft.values = [...draft.values, createNewGenreValue(draft.values.length, metadata.id)];
                  })
                );
                if (metadata && metadata.values && metadata.values.length >= 0)
                  setSelectedValueIndex(metadata.values.length);
              }}
              sx={{ mr: "12px !important" }}
              endIcon={<AddCircleOutlineRounded />}
            >
              {t("common:add")}
            </Button>
            {currentValue && (
              <Button
                sx={{ mr: "12px !important" }}
                onClick={() => {
                  setMetadata(
                    produce((draft) => {
                      draft.values = draft.values!.filter((x, i) => i !== selectedvalueIndex);
                    })
                  );
                  setSelectedValueIndex((x) => (x > 0 ? x - 1 : x));
                }}
                endIcon={<RemoveCircleOutlineRounded />}
                color="error"
              >
                {t("common:remove")}
              </Button>
            )}
            <Stack
              sx={{
                maxHeight: currentValue ? "408px" : "446px",
                overflowY: "scroll",
                ...theme.helpers(theme).scrollBar,
              }}
            >
              {metadata &&
                metadata.values?.map((x, i) => (
                  <ConfigCard
                    key={i}
                    selected={selectedvalueIndex === i}
                    text={x.label ?? "-"}
                    onClick={() => setSelectedValueIndex(i)}
                    hasData={
                      x.valueObject?.value?.genresByBroadcaster?.filter((g) => g.value !== null && g.value !== "")
                        .length > 0
                    }
                  />
                ))}
            </Stack>
          </Stack>
        </Grid>
        <Grid item md={7}>
          <Stack spacing={1} direction={"column"}>
            {currentValue && (
              <FormControlLabel
                label={t("field:label.onlyShowEnabledBroadcasters")}
                sx={{ color: "text.tier", ml: 0.5, mb: 1, height: "40px" }}
                checked={filterEnabledBroadcasters}
                control={<Checkbox />}
                onChange={handleFilterEnabledBroadcastersChange}
              />
            )}
            <Stack spacing={0.5} sx={{ maxHeight: "552px", overflowY: "scroll", ...theme.helpers(theme).scrollBar }}>
              {currentValue &&
                broadcasters
                  .filter((x) => !filterEnabledBroadcasters || x.enabled)
                  .map((x, i) => (
                    <Stack
                      borderRadius={1}
                      width={"100%"}
                      justifyContent={"space-between"}
                      display={"flex"}
                      direction={"row"}
                      alignItems={"center"}
                      pl={1}
                      pr={1}
                      pb={0.87}
                      pt={0.87}
                      sx={{ background: theme.palette.background[200] }}
                    >
                      <Typography
                        width={"calc(100% - 219px)"}
                        textOverflow={"ellipsis"}
                        overflow={"hidden"}
                        whiteSpace={"nowrap"}
                        lineHeight={"24px"}
                        variant="body2"
                      >
                        {x.name}
                      </Typography>

                      <OutlinedInput
                        sx={{ height: "24px" }}
                        value={
                          currentValue
                            ? currentValue.genresByBroadcaster.find((m) => m.broadcasterName === x.name)?.value ?? ""
                            : ""
                        }
                        onChange={(e) => {
                          setMetadata(
                            produce((draft) => {
                              const current =
                                draft.values &&
                                draft.values[selectedvalueIndex] &&
                                draft.values[selectedvalueIndex].valueObject?.value;
                              if (!current) return;
                              const val = e.target.value;
                              if ((!val || val === "") && current.genresByBroadcaster) {
                                current.genresByBroadcaster = current.genresByBroadcaster.filter(
                                  (m) => m.broadcasterName !== x.name
                                );
                                return;
                              }
                              const foundInMapping = current.genresByBroadcaster.find(
                                (m) => m.broadcasterName === x.name
                              );
                              if (foundInMapping) {
                                foundInMapping.value = val;
                              } else {
                                current.genresByBroadcaster = [
                                  ...current.genresByBroadcaster,
                                  {
                                    broadcasterName: x.name,
                                    value: val,
                                  },
                                ];
                              }
                            })
                          );
                        }}
                      />
                    </Stack>
                  ))}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </FormSection>
  );
};

export default GenreValuesForm;

const createNewGenreValue: (valueCount: number, metadataId: number) => DbMetadataFieldValueInfos = (
  valueCount: number,
  metadataId: number
) => ({
  $type: "VDM.AirLab.Sdk.Core.Database.Types.DbMetadataFieldValueInfos, VDM.AirLab.Sdk.Core",
  id: valueCount,
  externalId: 0,
  label: "",
  value: "",
  externalValue: null,
  rfc5646: null,
  valueObject: {
    $type: "VDM.AirLab.Sdk.Core.Database.Types.DbGenreMetadataValueInfos, VDM.AirLab.Sdk.Core",
    creationTime: new Date(),
    updateTime: null,
    fieldName: null,
    fieldLabel: null,
    targetLayer: null,
    valueLabel: null,
    fieldType: BaseMetadataType.DbGenreMetadataValueInfos,
    id: metadataId,
    value: {
      $type: "VDM.AirLab.Sdk.Core.Types.GenreTypes.GenreMetadataInfos, VDM.AirLab.Sdk.Core",
      name: "",
      genresByBroadcaster: [] as any[],
    },
  },
});
